// src/utils/validation.js

/**
 * 验证预约表单
 * @param {Object} form 预约表单数据
 * @param {Array} availableTimeSlots 可用时段列表
 * @param {Object} constraints 约束条件
 * @returns {Object} 验证结果 { isValid: boolean, errors: Array<string> }
 */
export const validateReservationForm = (form, availableTimeSlots, constraints) => {
    const errors = []

    // 基本字段验证
    if (!form.coachId) {
        errors.push('请选择教练')
    }

    if (!form.date) {
        errors.push('请选择日期')
    }

    if (!form.timeSlot) {
        errors.push('请选择时段')
    }

    // 预约次数限制
    if (constraints.isOverReservationLimit) {
        errors.push('今日预约次数已用完')
    }

    // 时段验证
    if (form.timeSlot) {
        const selectedSlot = availableTimeSlots.find(slot => slot.value === form.timeSlot)

        if (!selectedSlot) {
            errors.push('选择的时段不存在')
        } else {
            // 检查学员是否已预约该时段
            if (selectedSlot.studentAlreadyReserved) {
                errors.push('您已预约该时段，不可重复预约')
            }

            // 检查时段是否可用
            if (!selectedSlot.available) {
                if (selectedSlot.timeInvalid) {
                    errors.push(`请至少提前${constraints.minAdvanceHours}小时预约`)
                } else if (selectedSlot.isFull) {
                    errors.push('该时段已满员')
                } else {
                    errors.push(selectedSlot.reason || '该时段不可用')
                }
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    }
}

/**
 * 格式化错误信息
 * @param {Error} error 错误对象
 * @param {Object} options 选项
 * @returns {Object} 格式化后的错误信息 { message: string, type: string, action: string }
 */
export const formatError = (error = {}) => {
    let message = '预约失败，请稍后重试'
    let type = 'error'
    let action = null

    // 如果是请求超时
    if (error.message.includes('请求超时')) {
        return {
            message: '请求超时，请稍后重试',
            type: 'warning',
            action: null
        }
    }

    if (error.response && error.response.data) {
        const responseData = error.response.data

        // 处理字符串类型的错误消息
        if (typeof responseData === 'string') {
            if (responseData.includes('您已预约该时段')) {
                message = '您已预约该时段，不可重复预约'
                type = 'warning'
                action = 'all' // 刷新所有数据
            } else if (responseData.includes('超过单日最大预约次数')) {
                message = `${responseData}，请选择其他日期或联系管理员`
                type = 'warning'
                action = 'updateCount'
            } else if (responseData.includes('请至少提前')) {
                message = `预约时间不符合要求：${responseData}`
                type = 'warning'
            } else if (responseData.includes('超过可提前预约天数')) {
                message = `预约日期不符合要求：${responseData}`
                type = 'warning'
            } else if (responseData.includes('时间段已被预约') || responseData.includes('已满员')) {
                message = `该时段${responseData.includes('已满员') ? '已满员' : '已被其他学员预约'}，请重新选择时段`
                type = 'warning'
                action = 'refresh'
            } else {
                message = responseData
            }
        }
        // 处理对象类型的错误消息
        else if (responseData.message) {
            const errorMessage = responseData.message

            if (errorMessage.includes('您已预约该时段')) {
                message = '您已预约该时段，不可重复预约'
                type = 'warning'
                action = 'all'
            } else if (errorMessage.includes('超过单日最大预约次数')) {
                message = `${errorMessage}，请选择其他日期或联系管理员`
                type = 'warning'
                action = 'updateCount'
            } else if (errorMessage.includes('请至少提前')) {
                message = `预约时间不符合要求：${errorMessage}`
                type = 'warning'
            } else if (errorMessage.includes('超过可提前预约天数')) {
                message = `预约日期不符合要求：${errorMessage}`
                type = 'warning'
            } else if (errorMessage.includes('时间段已被预约') || errorMessage.includes('已满员')) {
                message = `该时段${errorMessage.includes('已满员') ? '已满员' : '已被其他学员预约'}，请重新选择时段`
                type = 'warning'
                action = 'refresh'
            } else {
                message = errorMessage
            }
        } else if (responseData.error) {
            message = responseData.error
        }

        // 检查HTTP状态码
        if (error.response.status === 500) {
            console.error('服务器内部错误，可能是重复预约或数据不一致:', responseData)
            // 如果是500错误且没有特定消息，建议刷新数据
            if (message === '预约失败，请稍后重试') {
                action = 'all'
            }
        }
    } else if (error.message) {
        message = error.message
    }

    // 网络相关错误
    if (message.includes('Network Error') || message.includes('网络连接失败')) {
        message = '网络连接失败，请检查网络连接后重试'
        type = 'error'
    }

    return { message, type, action }
}
